import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    RefreshCw, Search, Users, AlertCircle, MessageSquare, Calendar,
    Upload, Download, Flame, ListChecks, GitMerge, KanbanSquare, TableProperties,
    Tag, Plus, Trash2,
} from "lucide-react";

const STAGE_COLORS = {
    new_lead: "#94A3B8", contacted: "#3B82F6", demo_given: "#6366F1",
    payment_pending: "#F59E0B", onboarding: "#8B5CF6", live_customer: "#10B981",
    renewal_due: "#F97316", lost: "#EF4444",
};
const BAND_COLOR = { hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-700", cold: "bg-slate-100 text-slate-600" };

function StageChip({ stage, stages }) {
    const s = stages.find((x) => x.key === stage) || { label: stage, color: "#94A3B8" };
    return (
        <span
            className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: `${s.color}22`, color: s.color }}
        >{s.label}</span>
    );
}

function SumCard({ label, value, hint, color = "text-[#123B5D]", testId }) {
    return (
        <Card data-testid={testId}>
            <CardContent className="pt-4">
                <div className="text-[11px] uppercase tracking-widest text-[#17212B]/50">{label}</div>
                <div className={`text-2xl font-bold ${color}`}>{value ?? "—"}</div>
                {hint && <div className="text-[11px] text-[#17212B]/60 mt-0.5">{hint}</div>}
            </CardContent>
        </Card>
    );
}

// ── Contact 360 drawer ────────────────────────────────────────────────────
function RecordDrawer({ recordId, stages, tagCatalogue, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [tags, setTags] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState({ title: "", priority: "med", due_at: "" });

    const refresh = async () => {
        if (!recordId) return;
        try {
            const [rec, tl, tg, tk] = await Promise.all([
                api.get(`/crm/${recordId}`),
                api.get(`/crm/${recordId}/timeline`),
                api.get(`/crm/records/${recordId}/tags`),
                api.get(`/crm/tasks?record_id=${recordId}`),
            ]);
            setDetail(rec.data);
            setTimeline(tl.data.items || []);
            setTags(tg.data.items || []);
            setTasks(tk.data.items || []);
        } catch (e) { toast.error("Failed to load record"); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [recordId]);

    if (!recordId) return null;
    const r = detail?.record || {};

    const changeStage = async (stg) => {
        setSaving(true);
        try {
            await api.post(`/crm/${recordId}/move-stage`, { stage: stg });
            toast.success(`Stage → ${stg}`);
            refresh();
            onChanged?.();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setSaving(false); }
    };

    const saveNote = async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            await api.post(`/crm/${recordId}/notes`, { text: note });
            setNote(""); refresh();
        } catch { toast.error("Failed"); }
        finally { setSaving(false); }
    };

    const attachTag = async (tagId) => {
        try { await api.post(`/crm/records/${recordId}/tags/${tagId}`); refresh(); }
        catch { toast.error("Failed"); }
    };
    const detachTag = async (tagId) => {
        try { await api.delete(`/crm/records/${recordId}/tags/${tagId}`); refresh(); }
        catch { toast.error("Failed"); }
    };
    const addTask = async () => {
        if (!newTask.title.trim()) return;
        try {
            await api.post(`/crm/tasks`, {
                crm_record_id: recordId, title: newTask.title,
                priority: newTask.priority, due_at: newTask.due_at ? new Date(newTask.due_at).toISOString() : null,
            });
            setNewTask({ title: "", priority: "med", due_at: "" });
            refresh();
        } catch { toast.error("Failed to add task"); }
    };
    const toggleTask = async (t) => {
        try {
            await api.patch(`/crm/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
            refresh();
        } catch { toast.error("Failed"); }
    };
    const recomputeScore = async () => {
        try {
            const r = await api.get(`/crm/${recordId}/score`);
            toast.success(`Score ${r.data.score} · ${r.data.band}`);
            refresh(); onChanged?.();
        } catch { toast.error("Failed"); }
    };

    return (
        <Dialog open={!!recordId} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="crm-drawer">
                <DialogHeader>
                    <DialogTitle className="text-[#123B5D] flex items-center gap-3">
                        <span>{r.name || "(no name)"}</span>
                        <span className="text-[#17212B]/50 text-sm font-normal">· {r.email}</span>
                        {r.score !== undefined && r.score !== null && (
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${BAND_COLOR[r.score_band || "cold"]}`}
                                data-testid="crm-drawer-score">
                                {r.score_band} · {r.score}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-3 text-xs">
                    <div><b>Mobile:</b> {r.mobile || "—"}</div>
                    <div><b>City:</b> {r.city || "—"}</div>
                    <div><b>Specialization:</b> {r.specialization || "—"}</div>
                    <div><b>Source:</b> {r.source}</div>
                </div>
                <div className="flex items-center gap-3 py-3 border-y">
                    <span className="text-xs text-[#17212B]/60">Stage:</span>
                    <Select value={r.stage || ""} onValueChange={changeStage}>
                        <SelectTrigger className="w-52" data-testid="crm-stage-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={recomputeScore} data-testid="crm-recompute-score">
                        <Flame className="w-3.5 h-3.5 mr-1" />Recompute score
                    </Button>
                </div>

                {/* Tags */}
                <div className="border-b pb-3">
                    <div className="text-xs font-semibold text-[#123B5D] mb-2 flex items-center gap-2">
                        <Tag className="w-3.5 h-3.5" /> Tags
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {tags.map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold"
                                style={{ background: `${t.color}22`, color: t.color }} data-testid={`crm-tag-attached-${t.id}`}>
                                {t.name}
                                <button onClick={() => detachTag(t.id)} className="ml-1 hover:opacity-70">×</button>
                            </span>
                        ))}
                        {!tags.length && <span className="text-[11px] text-[#17212B]/50">No tags</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {(tagCatalogue || []).filter((t) => !tags.some((x) => x.id === t.id)).map((t) => (
                            <button key={t.id} className="text-[11px] px-2 py-0.5 rounded-full border border-dashed hover:border-solid"
                                style={{ borderColor: t.color, color: t.color }}
                                onClick={() => attachTag(t.id)} data-testid={`crm-tag-attach-${t.id}`}>
                                + {t.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tasks */}
                <div className="border-b pb-3">
                    <div className="text-xs font-semibold text-[#123B5D] mb-2 flex items-center gap-2">
                        <ListChecks className="w-3.5 h-3.5" /> Tasks ({tasks.length})
                    </div>
                    <div className="flex gap-2 mb-2">
                        <Input placeholder="Task title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                            data-testid="crm-task-title" className="flex-1 h-9" />
                        <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                            <SelectTrigger className="w-28 h-9" data-testid="crm-task-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="med">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input type="date" value={newTask.due_at} onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })}
                            className="w-36 h-9" data-testid="crm-task-due" />
                        <Button size="sm" onClick={addTask} data-testid="crm-task-add"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                        {tasks.map((t) => (
                            <div key={t.id} className="flex items-center gap-2 text-xs" data-testid={`crm-task-${t.id}`}>
                                <input type="checkbox" checked={t.status === "done"} onChange={() => toggleTask(t)}
                                    data-testid={`crm-task-toggle-${t.id}`} />
                                <span className={t.status === "done" ? "line-through text-[#17212B]/40" : ""}>{t.title}</span>
                                <span className="ml-auto text-[10px] px-1.5 rounded-full bg-[#F5F7FB]">{t.priority}</span>
                                {t.due_at && <span className="text-[10px] text-[#17212B]/50">{t.due_at.slice(0, 10)}</span>}
                            </div>
                        ))}
                        {!tasks.length && <div className="text-[11px] text-[#17212B]/50">No tasks yet.</div>}
                    </div>
                </div>

                {/* Notes composer */}
                <div className="border-b pb-3">
                    <div className="text-xs font-semibold text-[#123B5D] mb-2 flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Add note</div>
                    <div className="flex gap-2">
                        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Timestamped note…" data-testid="crm-note-input" />
                        <Button onClick={saveNote} disabled={saving || !note.trim()} data-testid="crm-note-save">Add</Button>
                    </div>
                </div>

                {/* Unified timeline */}
                <div>
                    <div className="text-xs font-semibold text-[#123B5D] mb-2 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Contact 360 timeline</div>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="crm-timeline">
                        {timeline.map((ev, i) => (
                            <div key={i} className="text-[11px] flex items-start gap-3 border-l-2 pl-3 py-1"
                                style={{ borderColor: STAGE_COLORS[ev.detail?.to] || "#E5E9F2" }}>
                                <span className="text-[#17212B]/50 w-28 shrink-0">{ev.at?.slice(0, 16).replace("T", " ")}</span>
                                <span className="uppercase tracking-widest text-[10px] font-bold w-14 shrink-0" style={{ color: "#0F766E" }}>{ev.kind}</span>
                                <span className="flex-1">
                                    <div className="font-medium">{ev.title}</div>
                                    <div className="text-[#17212B]/60 truncate">
                                        {typeof ev.detail === "string" ? ev.detail : JSON.stringify(ev.detail).slice(0, 200)}
                                    </div>
                                </span>
                                {ev.actor && <span className="text-[10px] text-[#17212B]/40">{ev.actor}</span>}
                            </div>
                        ))}
                        {!timeline.length && <div className="text-[11px] text-[#17212B]/50">No activity yet.</div>}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ── Kanban board ─────────────────────────────────────────────────────────
function KanbanBoard({ stages, onOpen }) {
    const [board, setBoard] = useState({ columns: [] });
    const [search, setSearch] = useState("");
    const [mineOnly, setMineOnly] = useState(false);

    const load = async () => {
        const q = new URLSearchParams();
        if (search) q.set("search", search);
        if (mineOnly) q.set("assigned_to_me", "1");
        q.set("per_stage", "25");
        try {
            const r = await api.get(`/crm/kanban?${q.toString()}`);
            setBoard(r.data);
        } catch (e) { toast.error("Failed to load kanban"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, mineOnly]);

    const onDragEnd = async (result) => {
        if (!result.destination) return;
        const { source, destination, draggableId } = result;
        if (source.droppableId === destination.droppableId) return;
        // Optimistic move
        setBoard((prev) => {
            const next = { columns: prev.columns.map((c) => ({ ...c, records: [...c.records] })) };
            const from = next.columns.find((c) => c.stage === source.droppableId);
            const to = next.columns.find((c) => c.stage === destination.droppableId);
            const idx = from.records.findIndex((r) => r.id === draggableId);
            if (idx < 0) return prev;
            const [moved] = from.records.splice(idx, 1);
            moved.stage = destination.droppableId;
            to.records.splice(destination.index, 0, moved);
            from.count -= 1; to.count += 1;
            return next;
        });
        try {
            await api.post(`/crm/${draggableId}/move-stage`, { stage: destination.droppableId });
            toast.success(`Moved to ${destination.droppableId}`);
        } catch (e) {
            toast.error("Move failed — reverting");
            load();
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#17212B]/40" />
                    <Input className="pl-8 h-9" placeholder="Search name / email / mobile"
                        value={search} onChange={(e) => setSearch(e.target.value)} data-testid="crm-kanban-search" />
                </div>
                <Button variant={mineOnly ? "default" : "outline"} onClick={() => setMineOnly(!mineOnly)}
                    data-testid="crm-kanban-mine">Assigned to me</Button>
                <Button variant="outline" onClick={load} data-testid="crm-kanban-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex gap-3 overflow-x-auto pb-4" data-testid="crm-kanban-board">
                    {board.columns.map((col) => (
                        <Droppable key={col.stage} droppableId={col.stage}>
                            {(prov, snap) => (
                                <div ref={prov.innerRef} {...prov.droppableProps}
                                    className={`w-72 shrink-0 rounded-lg border-2 p-2 transition-colors
                                     ${snap.isDraggingOver ? "border-solid" : "border-dashed border-transparent"}`}
                                    style={{ background: `${col.color}0F`, borderColor: snap.isDraggingOver ? col.color : "transparent" }}
                                    data-testid={`crm-kanban-col-${col.stage}`}>
                                    <div className="flex items-center justify-between px-2 mb-2">
                                        <div className="text-xs font-bold uppercase tracking-widest" style={{ color: col.color }}>{col.label}</div>
                                        <div className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                                            style={{ background: `${col.color}22`, color: col.color }}>
                                            {col.count}
                                        </div>
                                    </div>
                                    <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                                        {col.records.map((r, i) => (
                                            <Draggable key={r.id} draggableId={r.id} index={i}>
                                                {(p, s) => (
                                                    <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                                                        onClick={() => onOpen(r.id)}
                                                        className={`bg-white rounded p-2.5 border cursor-pointer shadow-sm hover:shadow-md
                                                        transition-shadow ${s.isDragging ? "shadow-lg" : ""}`}
                                                        style={{ ...p.draggableProps.style }}
                                                        data-testid={`crm-kanban-card-${r.id}`}>
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="font-medium text-[12px] text-[#123B5D] truncate">{r.name || "(no name)"}</div>
                                                            {r.score !== undefined && r.score !== null && (
                                                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${BAND_COLOR[r.score_band || "cold"]}`}>
                                                                    {r.score}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-[10px] text-[#17212B]/60 truncate">{r.email}</div>
                                                        {(r.city || r.specialization) && (
                                                            <div className="text-[10px] text-[#17212B]/50 mt-0.5 truncate">
                                                                {r.city}{r.city && r.specialization ? " · " : ""}{r.specialization}
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between mt-1.5">
                                                            <span className="text-[9px] uppercase text-[#17212B]/40">{r.source}</span>
                                                            {r.next_followup_at && (
                                                                <span className="text-[9px] text-amber-700 font-semibold">
                                                                    ⏰ {r.next_followup_at.slice(0, 10)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {prov.placeholder}
                                        {!col.records.length && (
                                            <div className="text-[10px] text-[#17212B]/40 text-center py-4">Empty</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    ))}
                </div>
            </DragDropContext>
        </div>
    );
}

// ── Tasks tab ────────────────────────────────────────────────────────────
function TasksTab({ onOpen }) {
    const [tasks, setTasks] = useState([]);
    const [filter, setFilter] = useState({ status: "open", mine: false, overdue: false });

    const load = async () => {
        const q = new URLSearchParams();
        if (filter.status && filter.status !== "all") q.set("status", filter.status);
        if (filter.mine) q.set("mine", "1");
        if (filter.overdue) q.set("overdue", "1");
        try { const r = await api.get(`/crm/tasks?${q.toString()}`); setTasks(r.data.items || []); }
        catch { toast.error("Failed to load tasks"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const toggle = async (t) => {
        await api.patch(`/crm/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
        load();
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                    <SelectTrigger className="w-36 h-9" data-testid="crm-tasks-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant={filter.mine ? "default" : "outline"} onClick={() => setFilter({ ...filter, mine: !filter.mine })}
                    data-testid="crm-tasks-mine">Mine</Button>
                <Button variant={filter.overdue ? "default" : "outline"} onClick={() => setFilter({ ...filter, overdue: !filter.overdue })}
                    data-testid="crm-tasks-overdue">Overdue</Button>
                <span className="text-xs text-[#17212B]/50 ml-auto">{tasks.length} tasks</span>
            </div>
            <div className="rounded-lg border overflow-x-auto bg-white">
                <table className="min-w-full text-sm" data-testid="crm-tasks-table">
                    <thead className="bg-[#123B5D] text-white text-xs uppercase">
                        <tr>
                            <th className="px-3 py-2 text-left w-8"></th>
                            <th className="px-3 py-2 text-left">Title</th>
                            <th className="px-3 py-2 text-left">Priority</th>
                            <th className="px-3 py-2 text-left">Due</th>
                            <th className="px-3 py-2 text-left">Assignee</th>
                            <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map((t, i) => (
                            <tr key={t.id} className={i % 2 ? "bg-[#F5F8FA]" : ""}>
                                <td className="px-3 py-2"><input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} /></td>
                                <td className="px-3 py-2 cursor-pointer text-[#123B5D] hover:underline"
                                    onClick={() => onOpen(t.crm_record_id)}>{t.title}</td>
                                <td className="px-3 py-2 text-[11px]">{t.priority}</td>
                                <td className="px-3 py-2 text-[11px]">{t.due_at?.slice(0, 10) || "—"}</td>
                                <td className="px-3 py-2 text-[11px]">{t.assignee_email || "—"}</td>
                                <td className="px-3 py-2 text-[11px]">{t.status}</td>
                            </tr>
                        ))}
                        {!tasks.length && (
                            <tr><td colSpan={6} className="text-center py-6 text-[#17212B]/60 text-xs">No tasks match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Duplicates tab ───────────────────────────────────────────────────────
function DuplicatesTab() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("pending");
    const load = async () => {
        try { const r = await api.get(`/crm/duplicates?status=${status}`); setItems(r.data.items || []); }
        catch { toast.error("Failed"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
    const resolve = async (id, action) => {
        try {
            await api.post(`/crm/duplicates/${id}/resolve`, { action });
            toast.success(`Resolved: ${action}`);
            load();
        } catch { toast.error("Failed"); }
    };
    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-40 h-9" data-testid="crm-dup-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="pending">Pending review</SelectItem>
                        <SelectItem value="merged">Merged</SelectItem>
                        <SelectItem value="kept_both">Kept both</SelectItem>
                        <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-xs text-[#17212B]/50">{items.length} rows</span>
            </div>
            <div className="space-y-2" data-testid="crm-duplicates-list">
                {items.map((d) => (
                    <Card key={d.id} data-testid={`crm-dup-${d.id}`}>
                        <CardContent className="pt-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="text-[11px] text-amber-700 font-semibold uppercase tracking-widest">
                                        {d.reason}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                                        <div>
                                            <div className="font-semibold text-[#123B5D] mb-1">Existing</div>
                                            <div>{d.existing?.name} · {d.existing?.email}</div>
                                            <div className="text-[#17212B]/60">📱 {d.existing?.mobile || "—"}</div>
                                            <div className="text-[#17212B]/60">Stage: {d.existing?.stage}</div>
                                        </div>
                                        <div>
                                            <div className="font-semibold text-[#123B5D] mb-1">Incoming</div>
                                            <div>{d.incoming_payload?.name} · {d.incoming_payload?.email}</div>
                                            <div className="text-[#17212B]/60">📱 {d.incoming_payload?.mobile || "—"}</div>
                                            <div className="text-[#17212B]/60">Source: {d.incoming_payload?.source}</div>
                                        </div>
                                    </div>
                                </div>
                                {status === "pending" && (
                                    <div className="flex flex-col gap-2">
                                        <Button size="sm" onClick={() => resolve(d.id, "merge_into_existing")} data-testid={`crm-dup-merge-${d.id}`}>
                                            <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge into existing
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => resolve(d.id, "keep_both")} data-testid={`crm-dup-keep-${d.id}`}>
                                            Keep both
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => resolve(d.id, "drop_incoming")} data-testid={`crm-dup-drop-${d.id}`}>
                                            Drop incoming
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {!items.length && (
                    <div className="text-center py-8 text-[#17212B]/60 text-xs">No duplicates in this state.</div>
                )}
            </div>
        </div>
    );
}

// ── Tools tab: Tags + Stages + Scoring + CSV ─────────────────────────────
function ToolsTab({ stages, tags, onRefresh }) {
    const fileRef = useRef(null);
    const [newTag, setNewTag] = useState({ name: "", color: "#6366F1", category: "custom" });
    const [busy, setBusy] = useState(false);
    const [scoringCfg, setScoringCfg] = useState(null);

    useEffect(() => {
        api.get("/crm/scoring-config").then((r) => setScoringCfg(r.data)).catch(() => {});
    }, []);

    const exportCsv = () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/crm/export.csv`;
        const token = localStorage.getItem("token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "crm.csv"; link.click();
            }).catch(() => toast.error("Export failed"));
    };
    const importCsv = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        setBusy(true);
        try {
            const r = await api.post("/crm/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Import: created=${r.data.created}, merged=${r.data.auto_merged}, flagged=${r.data.flagged}`);
            onRefresh();
        } catch { toast.error("Import failed"); }
        finally { setBusy(false); fileRef.current.value = ""; }
    };
    const createTag = async () => {
        if (!newTag.name.trim()) return;
        await api.post("/crm/tags", newTag);
        setNewTag({ name: "", color: "#6366F1", category: "custom" });
        onRefresh();
    };
    const deleteTag = async (id) => {
        if (!window.confirm("Delete tag? This detaches it from every record.")) return;
        await api.delete(`/crm/tags/${id}`); onRefresh();
    };
    const saveScoring = async () => {
        if (!scoringCfg) return;
        try {
            await api.put("/crm/scoring-config", {
                enabled: scoringCfg.enabled, hot_band: scoringCfg.hot_band,
                warm_band: scoringCfg.warm_band, rules: scoringCfg.rules,
            });
            toast.success("Scoring config saved");
        } catch { toast.error("Failed"); }
    };
    const recomputeAll = async () => {
        setBusy(true);
        try {
            const r = await api.post("/crm/scoring/recompute-all");
            toast.success(`Recomputed ${r.data.recomputed} records`);
        } catch { toast.error("Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* CSV */}
            <Card>
                <CardContent className="pt-4 space-y-3">
                    <h3 className="text-sm font-bold text-[#123B5D] flex items-center gap-2"><Upload className="w-4 h-4" />CSV Import / Export</h3>
                    <p className="text-[11px] text-[#17212B]/60">Anti-duplication: exact mobile match auto-merges; email-only conflict is queued to the Duplicates tab.</p>
                    <div className="flex gap-2">
                        <Button onClick={exportCsv} variant="outline" data-testid="crm-export-btn">
                            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                        </Button>
                        <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} style={{ display: "none" }} data-testid="crm-import-file" />
                        <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="crm-import-btn">
                            <Upload className="w-3.5 h-3.5 mr-1" /> Import CSV
                        </Button>
                    </div>
                    <div className="text-[10px] text-[#17212B]/50">
                        Expected columns: <code>email,name,mobile,city,specialization,source,stage,tags,notes</code>. Tags are ; separated.
                    </div>
                </CardContent>
            </Card>

            {/* Scoring */}
            <Card>
                <CardContent className="pt-4 space-y-3">
                    <h3 className="text-sm font-bold text-[#123B5D] flex items-center gap-2"><Flame className="w-4 h-4" />Lead scoring</h3>
                    {scoringCfg ? (
                        <div className="space-y-2 text-xs">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={!!scoringCfg.enabled}
                                    onChange={(e) => setScoringCfg({ ...scoringCfg, enabled: e.target.checked })}
                                    data-testid="crm-scoring-enabled" />
                                Enabled (rule-based; AI-scoring bolts on later)
                            </label>
                            <div className="flex gap-3 items-center">
                                <label className="flex items-center gap-1">Hot ≥
                                    <Input type="number" className="w-20 h-8"
                                        value={scoringCfg.hot_band || 0}
                                        onChange={(e) => setScoringCfg({ ...scoringCfg, hot_band: parseInt(e.target.value || "0", 10) })}
                                        data-testid="crm-scoring-hot" />
                                </label>
                                <label className="flex items-center gap-1">Warm ≥
                                    <Input type="number" className="w-20 h-8"
                                        value={scoringCfg.warm_band || 0}
                                        onChange={(e) => setScoringCfg({ ...scoringCfg, warm_band: parseInt(e.target.value || "0", 10) })}
                                        data-testid="crm-scoring-warm" />
                                </label>
                            </div>
                            <div className="text-[11px] text-[#17212B]/70">
                                <div className="font-semibold mb-1">Rules ({(scoringCfg.rules || []).length}):</div>
                                {(scoringCfg.rules || []).map((r, i) => (
                                    <div key={i} className="flex justify-between border-b border-dashed py-0.5">
                                        <span className="font-mono text-[10px]">{r.signal}</span>
                                        <span className="text-[10px]">+{r.weight} (cap {r.cap})</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button size="sm" onClick={saveScoring} data-testid="crm-scoring-save">Save config</Button>
                                <Button size="sm" variant="outline" onClick={recomputeAll} disabled={busy} data-testid="crm-scoring-recompute">
                                    Recompute all
                                </Button>
                            </div>
                        </div>
                    ) : <div className="text-[11px] text-[#17212B]/50">Loading…</div>}
                </CardContent>
            </Card>

            {/* Tags */}
            <Card className="lg:col-span-2">
                <CardContent className="pt-4 space-y-2">
                    <h3 className="text-sm font-bold text-[#123B5D] flex items-center gap-2"><Tag className="w-4 h-4" />Tag catalogue</h3>
                    <div className="flex gap-2 items-center">
                        <Input placeholder="New tag name" value={newTag.name}
                            onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                            className="w-56 h-9" data-testid="crm-new-tag-name" />
                        <Input type="color" value={newTag.color}
                            onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                            className="w-16 h-9 p-1" data-testid="crm-new-tag-color" />
                        <Button size="sm" onClick={createTag} data-testid="crm-new-tag-add">Add tag</Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-2">
                        {tags.map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold"
                                style={{ background: `${t.color}22`, color: t.color }} data-testid={`crm-tag-${t.id}`}>
                                {t.name}
                                <button className="ml-1 opacity-60 hover:opacity-100" onClick={() => deleteTag(t.id)}>
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                        {!tags.length && <span className="text-[11px] text-[#17212B]/50">No tags yet</span>}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ── List tab (original table) ────────────────────────────────────────────
function ListTab({ stages, onOpen }) {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({ stage: "all", source: "all", search: "", overdue: false });
    const [page, setPage] = useState(1);

    const load = async () => {
        try {
            const q = {
                ...(filters.stage !== "all" ? { stage: filters.stage } : {}),
                ...(filters.source !== "all" ? { source: filters.source } : {}),
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.overdue ? { overdue: 1 } : {}),
                page, page_size: 25,
            };
            const r = await api.get("/crm", { params: q });
            setRecords(r.data.items); setTotal(r.data.total);
        } catch (e) { toast.error("Failed"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page]);
    return (
        <div>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <div className="flex-1 min-w-[220px]">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#17212B]/40" />
                        <Input className="pl-8 h-9" placeholder="Email / name / mobile"
                            value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            data-testid="crm-search" />
                    </div>
                </div>
                <Select value={filters.stage} onValueChange={(v) => setFilters({ ...filters, stage: v })}>
                    <SelectTrigger className="w-40 h-9" data-testid="crm-filter-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All stages</SelectItem>
                        {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button variant={filters.overdue ? "default" : "outline"} onClick={() => setFilters({ ...filters, overdue: !filters.overdue })} data-testid="crm-filter-overdue">
                    <AlertCircle className="w-3.5 h-3.5 mr-1" /> Overdue only
                </Button>
            </div>
            <div className="rounded-lg border border-[#123B5D]/10 bg-white overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="crm-table">
                    <thead className="bg-[#123B5D] text-white text-xs uppercase">
                        <tr>
                            <th className="px-3 py-2 text-left">Name / Email</th>
                            <th className="px-3 py-2 text-left">Score</th>
                            <th className="px-3 py-2 text-left">City / Spec.</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-left">Stage</th>
                            <th className="px-3 py-2 text-left">Follow-up</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((r, i) => (
                            <tr key={r.id} className={(i % 2 ? "bg-[#F5F8FA] " : "") + "cursor-pointer hover:bg-[#0F766E]/5"}
                                onClick={() => onOpen(r.id)} data-testid={`crm-row-${r.id}`}>
                                <td className="px-3 py-2">
                                    <div className="font-medium text-[#123B5D]">{r.name || "(no name)"}</div>
                                    <div className="text-[11px] text-[#17212B]/60">{r.email}</div>
                                </td>
                                <td className="px-3 py-2">
                                    {r.score !== undefined && r.score !== null ? (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${BAND_COLOR[r.score_band || "cold"]}`}>
                                            {r.score_band} · {r.score}
                                        </span>
                                    ) : "—"}
                                </td>
                                <td className="px-3 py-2 text-[12px]">
                                    <div>{r.city || "—"}</div>
                                    <div className="text-[11px] text-[#17212B]/50">{r.specialization || ""}</div>
                                </td>
                                <td className="px-3 py-2 text-[11px] text-[#17212B]/60">{r.source}</td>
                                <td className="px-3 py-2"><StageChip stage={r.stage} stages={stages} /></td>
                                <td className="px-3 py-2 text-[11px]">{r.next_followup_at ? r.next_followup_at.slice(0, 10) : "—"}</td>
                            </tr>
                        ))}
                        {!records.length && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-[#17212B]/60">No records match.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-[#17212B]/60">
                <div>Total: <b>{total}</b> records</div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="crm-prev">Prev</Button>
                    <span className="pt-1.5">Page {page}</span>
                    <Button size="sm" variant="outline" disabled={records.length < 25} onClick={() => setPage((p) => p + 1)} data-testid="crm-next">Next</Button>
                </div>
            </div>
        </div>
    );
}

// ── Main page shell ──────────────────────────────────────────────────────
export default function SuperAdminCRM() {
    const [summary, setSummary] = useState(null);
    const [stages, setStages] = useState([]);
    const [tags, setTags] = useState([]);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("kanban");

    const refresh = async () => {
        try {
            const [s, st, tg] = await Promise.all([
                api.get("/crm/summary"),
                api.get("/crm/stages"),
                api.get("/crm/tags"),
            ]);
            setSummary(s.data);
            setStages(st.data.items || []);
            setTags(tg.data.items || []);
        } catch (e) { /* silent — likely first-time seed */ }
    };
    useEffect(() => { refresh(); }, []);

    const ingest = async () => {
        setBusy(true);
        try {
            const r = await api.post("/crm/ingest/refresh");
            toast.success(`Ingested — ${r.data.total_records} total records`);
            refresh();
        } catch { toast.error("Ingest failed"); }
        finally { setBusy(false); }
    };

    return (
        <AdminShell>
            <div className="mb-4 flex items-start justify-between" data-testid="crm-header">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin · CRM</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">Customer pipeline</h1>
                    <p className="text-[#17212B]/60 mt-1 text-sm">Configurable stages · rule-based scoring · Kanban · anti-duplication · CSV import/export.</p>
                </div>
                <Button variant="outline" onClick={ingest} disabled={busy} data-testid="crm-ingest">
                    <RefreshCw className="w-4 h-4 mr-1" />Refresh from sources
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <SumCard testId="crm-sum-total" label="Total records" value={summary?.total} />
                <SumCard testId="crm-sum-new-week" label="New this week" value={summary?.new_this_week} color="text-emerald-700" />
                <SumCard testId="crm-sum-overdue" label="Overdue follow-ups" value={summary?.overdue_followups} color="text-amber-700" hint="Set follow-ups on records" />
                <SumCard testId="crm-sum-live" label="Live customers" value={summary?.by_stage?.live_customer} color="text-[#0F766E]" />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="crm-tabs">
                    <TabsTrigger value="kanban" data-testid="crm-tab-kanban"><KanbanSquare className="w-3.5 h-3.5 mr-1" />Kanban</TabsTrigger>
                    <TabsTrigger value="list" data-testid="crm-tab-list"><TableProperties className="w-3.5 h-3.5 mr-1" />List</TabsTrigger>
                    <TabsTrigger value="tasks" data-testid="crm-tab-tasks"><ListChecks className="w-3.5 h-3.5 mr-1" />Tasks</TabsTrigger>
                    <TabsTrigger value="duplicates" data-testid="crm-tab-duplicates"><GitMerge className="w-3.5 h-3.5 mr-1" />Duplicates</TabsTrigger>
                    <TabsTrigger value="tools" data-testid="crm-tab-tools"><Users className="w-3.5 h-3.5 mr-1" />Tools</TabsTrigger>
                </TabsList>
                <TabsContent value="kanban" className="mt-4"><KanbanBoard stages={stages} onOpen={setSelected} /></TabsContent>
                <TabsContent value="list" className="mt-4"><ListTab stages={stages} onOpen={setSelected} /></TabsContent>
                <TabsContent value="tasks" className="mt-4"><TasksTab onOpen={setSelected} /></TabsContent>
                <TabsContent value="duplicates" className="mt-4"><DuplicatesTab /></TabsContent>
                <TabsContent value="tools" className="mt-4"><ToolsTab stages={stages} tags={tags} onRefresh={refresh} /></TabsContent>
            </Tabs>

            <RecordDrawer recordId={selected} stages={stages} tagCatalogue={tags}
                onClose={() => setSelected(null)} onChanged={refresh} />
        </AdminShell>
    );
}
